import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Search, 
  Plus, 
  Eye,
  Edit,
  Trash2,
  Users,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Target,
  QrCode
} from 'lucide-react';
import api from '@/services/api';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';
import QRCode from 'qrcode';

// Helper function to check if manager can edit a collaborateur
const canManagerEdit = (currentUser, collab) => {
  // Admin, HSE, RH can edit anyone
  if (['Admin', 'HSE', 'RH'].includes(currentUser?.role)) {
    return true;
  }
  // Manager can only edit their team members
  if (currentUser?.role === 'Manager') {
    return collab.manager_id === currentUser.id;
  }
  // Other roles cannot edit
  return false;
};
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const getStatutBadge = (collaborateur) => {
  const hasValidCACES = collaborateur.caces?.some(c => c.statut === 'valide');
  const hasValidVisite = collaborateur.visitesMedicales?.some(v => v.statut === 'valide');
  const hasActiveAuth = collaborateur.nb_autorisations > 0;
  
  if (hasActiveAuth && hasValidCACES && hasValidVisite) {
    return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Complet</Badge>;
  } else if (hasValidCACES || hasValidVisite) {
    return <Badge className="bg-orange-100 text-orange-800"><AlertTriangle className="h-3 w-3 mr-1" />Partiel</Badge>;
  }
  return <Badge className="bg-gray-100 text-gray-800"><XCircle className="h-3 w-3 mr-1" />Incomplet</Badge>;
};

export default function Collaborateurs() {
  const { secteurs, currentUser } = useApp();
  const navigate = useNavigate();
  const [collaborateurs, setCollaborateurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [secteurFilter, setSecteurFilter] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCollab, setSelectedCollab] = useState(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [qrCollab, setQrCollab] = useState(null);

  const canManageCompetences = ['Admin', 'RH', 'Manager'].includes(currentUser?.role);

  useEffect(() => {
    loadCollaborateurs();
  }, []);

  const loadCollaborateurs = async () => {
    try {
      setLoading(true);
      const data = await api.getCollaborateurs();
      setCollaborateurs(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des collaborateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCollab) return;
    
    try {
      await api.deleteCollaborateur(selectedCollab.id);
      toast.success('Collaborateur supprimé');
      loadCollaborateurs();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedCollab(null);
    }
  };

  const handleShowQR = async (collab) => {
    try {
      const frontendUrl = window.location.origin;
      const verificationUrl = `${frontendUrl}/verification-competences/${collab.id}`;
      const qrDataUrl = await QRCode.toDataURL(verificationUrl, { 
        width: 256,
        margin: 2,
        color: { dark: '#000000', light: '#ffffff' }
      });
      setQrCodeUrl(qrDataUrl);
      setQrCollab(collab);
      setQrDialogOpen(true);
    } catch (error) {
      console.error('Error generating QR code:', error);
      toast.error('Erreur lors de la génération du QR code');
    }
  };

  // Filter and sort collaborateurs alphabetically by nom then prenom
  const filteredCollaborateurs = useMemo(() => {
    return collaborateurs
      .filter(collab => {
        const matchesSearch = !search || 
          collab.nom?.toLowerCase().includes(search.toLowerCase()) ||
          collab.prenom?.toLowerCase().includes(search.toLowerCase()) ||
          collab.matricule?.toLowerCase().includes(search.toLowerCase()) ||
          collab.email?.toLowerCase().includes(search.toLowerCase());
        
        const matchesSecteur = secteurFilter === 'all' || collab.secteur === secteurFilter;
        
        return matchesSearch && matchesSecteur;
      })
      .sort((a, b) => {
        // Sort by nom first
        const nomCompare = (a.nom || '').localeCompare(b.nom || '', 'fr', { sensitivity: 'base' });
        if (nomCompare !== 0) return nomCompare;
        // If same nom, sort by prenom
        return (a.prenom || '').localeCompare(b.prenom || '', 'fr', { sensitivity: 'base' });
      });
  }, [collaborateurs, search, secteurFilter]);

  // Stats
  const stats = {
    total: collaborateurs.length,
    actifs: collaborateurs.filter(c => c.actif).length,
    avecAutorisations: collaborateurs.filter(c => c.nb_autorisations > 0).length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Collaborateurs</h1>
          <p className="text-gray-500">Gestion des collaborateurs et de leurs certifications</p>
        </div>
        <Link to="/collaborateurs/nouveau">
          <Button className="gap-2">
            <Plus className="h-4 w-4" />
            Nouveau collaborateur
          </Button>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-blue-100">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total</p>
                <p className="text-xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-green-100">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Actifs</p>
                <p className="text-xl font-bold">{stats.actifs}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-purple-100">
                <CheckCircle className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Avec autorisations</p>
                <p className="text-xl font-bold">{stats.avecAutorisations}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Rechercher par nom, matricule, email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={secteurFilter} onValueChange={setSecteurFilter}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Tous les secteurs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les secteurs</SelectItem>
                {secteurs.map((secteur) => (
                  <SelectItem key={secteur.id} value={secteur.libelle}>
                    {secteur.libelle}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredCollaborateurs.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">Aucun collaborateur trouvé</p>
              <Link to="/collaborateurs/nouveau">
                <Button variant="link" className="mt-2">
                  Ajouter un collaborateur
                </Button>
              </Link>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Matricule</TableHead>
                  <TableHead>Nom</TableHead>
                  <TableHead>Secteur</TableHead>
                  <TableHead>Poste</TableHead>
                  <TableHead>Habilitations</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCollaborateurs.map((collab) => (
                  <TableRow key={collab.id} data-testid={`collab-row-${collab.id}`}>
                    <TableCell className="font-medium">{collab.matricule}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{collab.nom} {collab.prenom}</p>
                        <p className="text-sm text-gray-500">{collab.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>{collab.secteur}</TableCell>
                    <TableCell>{collab.poste}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {/* Afficher les CACES */}
                        {collab.caces?.slice(0, 2).map((c, i) => (
                          <Badge key={`caces-${i}`} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                            {c.categorie_code}
                          </Badge>
                        ))}
                        {/* Afficher les autorisations de conduite */}
                        {collab.autorisations?.slice(0, 2).map((auth, i) => (
                          <Badge key={`auth-${i}`} variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                            {auth.vehicule_type}
                          </Badge>
                        ))}
                        {/* Afficher "+X" si plus de 4 habilitations au total */}
                        {(collab.caces?.length + collab.autorisations?.length) > 4 && (
                          <Badge variant="outline" className="text-xs bg-gray-50">
                            +{(collab.caces?.length || 0) + (collab.autorisations?.length || 0) - 4}
                          </Badge>
                        )}
                        {/* Afficher un message si aucune habilitation */}
                        {(!collab.caces || collab.caces.length === 0) && (!collab.autorisations || collab.autorisations.length === 0) && (
                          <span className="text-xs text-gray-400">Aucune</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{getStatutBadge(collab)}</TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Link to={`/collaborateurs/${collab.id}`}>
                          <Button variant="ghost" size="icon" title="Voir détails">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </Link>
                        {/* Competences button */}
                        {canManageCompetences && (
                          <Button 
                            variant="ghost" 
                            size="icon"
                            title="Compétences"
                            onClick={() => navigate(`/collaborateurs/${collab.id}/competences`)}
                          >
                            <Target className="h-4 w-4 text-blue-500" />
                          </Button>
                        )}
                        {/* QR Code button */}
                        <Button 
                          variant="ghost" 
                          size="icon"
                          title="QR Code vérification"
                          onClick={() => handleShowQR(collab)}
                        >
                          <QrCode className="h-4 w-4 text-purple-500" />
                        </Button>
                        {/* Edit button - only visible if user can edit this collaborateur */}
                        {canManagerEdit(currentUser, collab) && (
                          <Link to={`/collaborateurs/${collab.id}/modifier`}>
                            <Button variant="ghost" size="icon" title="Modifier">
                              <Edit className="h-4 w-4" />
                            </Button>
                          </Link>
                        )}
                        {/* Delete button - only for Admin */}
                        {currentUser?.role === 'Admin' && (
                          <Button 
                            variant="ghost" 
                            size="icon"
                            title="Supprimer"
                            onClick={() => {
                              setSelectedCollab(collab);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer le collaborateur{' '}
              <strong>{selectedCollab?.prenom} {selectedCollab?.nom}</strong> ?
              Cette action est irréversible et supprimera également tous ses CACES, visites médicales, formations et autorisations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              QR Code - {qrCollab?.prenom} {qrCollab?.nom}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            {qrCodeUrl && (
              <img 
                src={qrCodeUrl} 
                alt="QR Code vérification compétences" 
                className="w-64 h-64 border rounded-lg"
              />
            )}
            <p className="text-sm text-gray-500 text-center">
              Scannez ce QR code pour vérifier les compétences de ce collaborateur pour une machine.
            </p>
            <p className="text-xs text-gray-400 text-center">
              ⚠️ L'utilisateur doit être connecté à l'application pour accéder à la vérification.
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  const link = document.createElement('a');
                  link.download = `qr-${qrCollab?.matricule || 'collaborateur'}.png`;
                  link.href = qrCodeUrl;
                  link.click();
                }}
              >
                Télécharger
              </Button>
              <Button onClick={() => setQrDialogOpen(false)}>
                Fermer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
